import type { FlowProcessMap } from '@/features/flow/Flow';

import { isObject } from 'lodash-es';

import { provide, ref } from 'vue';
import { buildFlowRouter } from '@/features/flow/Flow';

import { useFlowEventEmitter, useGlobalEventEmitter } from '@/shared/composables';
import { Errors } from '@/shared/constants';
import { safeInject } from '@/shared/utils';
import { isAxiosError } from './guards';

export function useFlowController() {
  safeInject<() => void>('onFlowInit')();

  const flowOrderMap = safeInject<FlowProcessMap>('flowOrderMap');
  const productName = safeInject<string>('productName');

  const pathHead = safeInject<string>('pathHead');
  const flowOrder = flowOrderMap[productName];
  const landingUrl = safeInject<string>('landingUrl');
  const flowRouter = buildFlowRouter(pathHead, flowOrder, landingUrl);
  provide('flowRouter', flowRouter);

  const error = ref<{ message: string; code: string; originalError: unknown } | null>();
  const loading = ref(false);
  // TODO: временное решение. Вынести первый экран загрузки на страницу RenewInsurance и удалить флаг isMainLoader
  const isMainLoader = ref(false);

  const resolveError = () => {
    error.value = null;
  };

  const handleOnBack = async () => {
    await flowRouter.prevStep();
    resolveError();
  };

  const handleError = (generalError: unknown) => {
    if (isAxiosError(generalError)) {
      error.value = {
        message: generalError.errorMessage,
        code: generalError.errorCode,
        originalError: generalError,
      };
    } else if (generalError instanceof Error) {
      console.log(generalError);
      error.value = {
        message: generalError.message,
        code: '',
        originalError: generalError,
      };
    } else {
      error.value = {
        message:
          isObject(generalError) && 'message' in generalError
            ? generalError.message as string
            : String(generalError),
        code: '',
        originalError: generalError,
      };
    }
  };

  const requestPool = new Set();

  const onLoading = async (
    request: () => Promise<any>,
    requestId = 'request',
    withLoader = true,
    withError = true,
    infinityLoader = false,
    isFirstLoader: boolean,
  ) => {
    const routeFrom = flowRouter.currentStep.value.path;
    requestPool.add(requestId);
    if (withLoader) {
      loading.value = true;
      isMainLoader.value = isFirstLoader;
    }
    try {
      await request();
    } catch (error) {
      if (routeFrom === flowRouter.currentStep.value.path && withError) {
        handleError(error);
      }
    }

    if (infinityLoader) return;
    requestPool.delete(requestId);
    if (requestPool.size > 0) return;
    loading.value = false;
    isMainLoader.value = false;
  };

  const flowEventEmitter = useFlowEventEmitter();

  flowEventEmitter.on(
    'loading',
    async ({
      request,
      requestId = 'request',
      withLoader = true,
      withError = true,
      infinityLoader = false,
      isFirstLoader = false,
    }) => await onLoading(request, requestId, withLoader, withError, infinityLoader, isFirstLoader),
  );

  const eventEmitter = useGlobalEventEmitter();
  eventEmitter.on(Errors.API_BFF_ERROR, handleError);
  eventEmitter.on(Errors.API_BE_ERROR, handleError);
  eventEmitter.on(Errors.NO_NAME_ERROR, handleError);

  return {
    handleOnBack,
    error,
    handleError,
    resolveError,
    loading,
    onLoading,
    isMainLoader,
    landingUrl,
  };
}
